import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import Loading from '../../components/Loading'
import useEntity from '../../hooks/useEntity'
import BaseList, { baseListStyles } from '../../components/List/BaseList'
import useUser from '../../hooks/useUser'
import { compareDate, formatDifferenceInTime, parseDateTime } from '../../helpers/dateHelper'
import { Billable, TurnOverItem } from '../../config/registration/timeConfig'
import { Button, Divider, Label, MessageBar, Toolbar, ToolbarGroup } from '@fluentui/react-components'
import { ArrowLeftRegular, MailInboxRegular, SendRegular } from '@fluentui/react-icons'
import { MoneyDto } from '../../config/i18nConfig'
import TimeSuggestionDialog, { TrackTimeSuggestionDto } from '../../components/Dialog/TimeSuggestion/TimeSuggestionDialog'
import { getTheme, Icon, ITheme, mergeStyleSets, Stack, StackItem, Text } from '@fluentui/react'
import { IFileDto, IOverviewItemDto } from '../../config/fileConfig'
import { UserListItemDto } from '../../config/userConfig'
import { TimeListItemDto } from '../../config/registrationConfig'
import { Link } from 'react-router-dom'
import MoneyText from '../../components/Text/Currency/MoneyText'
import Tooltip from '../../components/Tooltip'
import { useBoolean } from '@fluentui/react-hooks'
import FormattedDateTime from '../../components/Date/FormattedDateTime'
import { useEmailToolbarStyles } from '../../components/Toolbar/Email/EmailToolbar'
import IntervalToolbarMenu, { getIntervalDate, Interval, IntervalProps } from '../../components/Toolbar/IntervalToolbarMenu'
import { useTranslation } from 'react-i18next'
import { LocalizedValueSetting, TimeSuggestionDescriptionOptionEnum } from '../../context/GlobalContext'
import { useSettings } from '../../hooks/useSettings'
import { getTimeTurnover } from './BaseTimeOverview'
import { useSelector } from 'react-redux'
import { Store } from '../../reducers'

export enum TimeSuggestionStatus {
    ToReview = 'TO_REVIEW',
    NoTracking = 'NO_TRACKING',
    Tracked = 'TRACKED',
}

export type TimeSuggestionOverviewDto = {
    id: string
    file: IOverviewItemDto
    user: UserListItemDto
    time: TimeListItemDto
    internetMessageId: string
    emailCreated: string
    emailSent: string
    senderName: string
    senderEmail: string
    recipientsName: string
    recipientsEmail: string
    emailInbound: boolean
    subject: string
    status: TimeSuggestionStatus
}

//Currently there is only one type of TimeSuggestion, but this can be extended to handle multiple types.
type BaseItem = TimeSuggestionOverviewDto

export type OverviewItem = {
    // These fields are calculated during runtime and not actually returned by the API.
    title: string
    subTitle: string
    turnover: MoneyDto // Expressed in American number notation (0.00).
} & BaseItem &
    TurnOverItem

/**
 * Map all dtos to one OverviewItem type.
 *
 * @param dtos the dtos received from the server.
 */
const mapTimeSuggestions = (dtos: BaseItem[]): OverviewItem[] =>
    dtos.map((dto: BaseItem) => mapTimeSuggestion(dto)).map((dto: OverviewItem) => addOverviewItemTurnover(dto))

const mapTimeSuggestion = (dto: BaseItem): Partial<OverviewItem> => {
    if (isEmailTimeSuggestion(dto)) {
        return mapEmailTimeSuggestion(dto)
    }

    return {
        billable: Billable.Billable,
        title: 'Onbekende tijdsuggestie type',
        ...(dto as BaseItem),
    }
}

/**
 * Determines if the BaseItem is a TimeSuggestion of an e-mail.
 *
 * @param item the item used in the predicate.
 */
export const isEmailTimeSuggestion = (item: BaseItem): item is TimeSuggestionOverviewDto => {
    return (item as TimeSuggestionOverviewDto).internetMessageId !== undefined
}

/**
 * Maps the BaseItem to a partial OverviewItem describing an e-mail.
 *
 * @param dto the item used to map to an OverviewItem.
 */
const mapEmailTimeSuggestion = (dto: TimeSuggestionOverviewDto): OverviewItem => ({
    billable: Billable.Billable,
    duration: formatDifferenceInTime(dto.emailCreated, dto.emailSent, '00:01'),
    title: dto.emailInbound ? dto.senderName : dto.recipientsName || '',
    subTitle: dto.subject || '',
    turnover: undefined,
    ...dto,
})

/**
 * Adds the turnover of the item.
 *
 * @param dto the item used to calculate the turnover.
 * @return the item + the calculated turnover.
 */
const addOverviewItemTurnover = (dto: OverviewItem): OverviewItem => ({
    ...dto,
    turnover: getTimeTurnover({ amount: dto.file.rate, currency: dto.file.currency }, dto),
})

/**
 * Converts an OverviewDto to a TrackTimeSuggestionDto. This is used for filling the dialog upon click.
 *
 * @param item the source item coming from the server.
 * @param language the language currently used by the user.
 * @param setting the settings containing the description template used by the customer.
 * @return the converted item used in the dialog.
 */
export const mapOverviewItemToTrackTimeSuggestionDto = (
    item: OverviewItem,
    language: string,
    setting?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[],
): Partial<TrackTimeSuggestionDto> => ({
    timeSuggestionId: item.id,
    fileId: item.file.id,
    date: parseDateTime(item.emailSent).toFormat('yyyy-LL-dd'),
    description: getOverviewItemDescription(item, language, setting),
    duration: item.duration,
    emailInbound: item.emailInbound,
})

/**
 * Determines the description of the OverviewDto. Currently only used for e-mails, but can be extended for other types.
 *
 * @param item the item used to determine the description.
 * @param language the language currently used by the user.
 * @param setting the settings containing the description template used by the customer.
 */
const getOverviewItemDescription = (
    item: OverviewItem,
    language: string,
    setting?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[],
): string => {
    if (isEmailTimeSuggestion(item)) {
        return getEmailTimeSuggestionDescription(item as TimeSuggestionOverviewDto, language, setting)
    }

    return ''
}

/**
 * Determines the description of the OverviewDto. Currently only used for e-mails, but can be extended for other types.
 *
 * @param item the item used to determine the description.
 * @param language the language currently used by the user.
 * @param setting the settings containing the description template used by the customer.
 */
export const getEmailTimeSuggestionDescription = (
    item: TimeSuggestionOverviewDto,
    language: string,
    setting?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[],
): string => {
    const time = parseDateTime(item.emailSent).toFormat('HH:mm')

    let description =
        setting
            ?.find(name => name.key.toLowerCase() === language.toLowerCase())
            ?.value?.find(
                option =>
                    option.key === (item.emailInbound ? TimeSuggestionDescriptionOptionEnum.EmailIn : TimeSuggestionDescriptionOptionEnum.EmailOut),
            ).value || ''

    description = description.replaceAll('${time}', time)
    description = description.replaceAll('${subject}', item.subject)
    description = description.replaceAll('${sender}', item.senderName)
    description = description.replaceAll('${recipients}', item.recipientsName)
    description = description.replaceAll('${file.name}', item.file.name)
    description = description.replaceAll('${file.reference}', item.file.reference)
    return description
}

function Header(props: IntervalProps): ReactElement {
    const styles = useEmailToolbarStyles()
    const { t: translate } = useTranslation()

    return (
        <Stack tokens={{ childrenGap: 's2' }} horizontal={false}>
            <StackItem align="start">
                <Button icon={<ArrowLeftRegular />} onClick={() => history.back()}>
                    {translate('action.time.suggestion.back')}
                </Button>
            </StackItem>
            <StackItem>
                <Toolbar size="medium" className={styles.toolbar}>
                    <ToolbarGroup role="items"></ToolbarGroup>
                    <ToolbarGroup role="farItems">
                        <IntervalToolbarMenu {...props} />
                    </ToolbarGroup>
                </Toolbar>
            </StackItem>
        </Stack>
    )
}

function NoData(): ReactElement {
    return (
        <Stack tokens={{ padding: 's1', childrenGap: 's1' }}>
            <StackItem>
                <Divider />
            </StackItem>
            <StackItem>
                <Label>Er zijn momenteel geen tijdsuggesties.</Label>
            </StackItem>
        </Stack>
    )
}

type OverviewListItemProps = {
    index: number
    item?: OverviewItem
    setInitialDialogValues: (value: Partial<TrackTimeSuggestionDto>) => void
    setShowDialog: () => void
}

const onSelectionChange = (
    props: OverviewListItemProps,
    language: string,
    setting?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[],
) => {
    if (props.item !== undefined) {
        const trackTimeSuggestion = mapOverviewItemToTrackTimeSuggestionDto(props.item, language, setting)
        props.setInitialDialogValues(trackTimeSuggestion)
        props.setShowDialog()
    }
}

const theme: ITheme = getTheme()
const styles = mergeStyleSets({
    date: { whiteSpace: 'nowrap' },
    snippet: { color: theme.palette.neutralSecondary },
    growNoWrap: { flexGrow: 1, minWidth: 0 },
})

function OverviewListItem(props: OverviewListItemProps): ReactElement {
    const { i18n } = useTranslation()
    const settings = useSettings()

    const item = props.item
    return (
        <Stack styles={{ root: baseListStyles.itemCell }} onClick={() => onSelectionChange(props, i18n.language, settings.timeSuggestionDescription)}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 's2' }}>
                {item.emailInbound ? <MailInboxRegular /> : <SendRegular />}
                <Text variant="medium" nowrap className={styles.growNoWrap}>
                    {item.emailInbound ? item?.senderName : item?.recipientsName || ''}
                </Text>
                <FormattedDateTime date={item.emailSent} />
            </Stack>
            <Stack horizontal verticalFill verticalAlign="center" tokens={{ childrenGap: 's2' }}>
                <Text variant="medium" block nowrap className={styles.growNoWrap}>
                    {item?.subject || ''}
                </Text>
            </Stack>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 's1' }}>
                <Tooltip hostProps={{ content: `${item.file.reference}: ${item.file.name}` }}>
                    <Link to={`/file/${item.file.id}`}>{item.file.reference}</Link>
                </Tooltip>
                <Stack horizontal tokens={{ childrenGap: 's2' }}>
                    <Icon iconName={'Clock'} />
                    <Text variant="small" className={styles.date}>
                        {item.duration}
                    </Text>
                </Stack>
                <MoneyText value={item.turnover} />
            </Stack>
        </Stack>
    )
}

function TimeSuggestionList({ timeSuggestions, setInitialDialogValues, setShowDialog }): ReactElement {
    return (
        <>
            {timeSuggestions.data.length === 0 && <NoData />}

            {timeSuggestions.data.length > 0 && (
                <BaseList<OverviewItem> items={timeSuggestions.data}>
                    {(item?: OverviewItem, index?: number) => (
                        <OverviewListItem item={item} index={index} setInitialDialogValues={setInitialDialogValues} setShowDialog={setShowDialog} />
                    )}
                </BaseList>
            )}
        </>
    )
}

export default function TimeSuggestionOverview(): ReactElement {
    const { currentUser } = useUser()
    const currentFile = useSelector<Store, IFileDto>(state => state.files.current.data)

    const [interval, setInterval] = useState<Interval>(Interval.Month)
    const [timeSuggestions, , resetTimeSuggestions] = useEntity<OverviewItem[], TimeSuggestionOverviewDto[]>(
        () => ({
            method: 'GET',
            url: '/time-suggestion',
            params: {
                itemUsers: [currentUser.id],
                files: currentFile ? [currentFile.id] : undefined,
                statuses: [TimeSuggestionStatus.ToReview],
                from: getIntervalDate(getInterval())?.toISODate(),
            },
        }),
        interval,
        result => mapTimeSuggestions(result.data).sort((a: OverviewItem, b: OverviewItem) => compareDate(b.emailSent, a.emailSent)),
        [],
    )

    const getInterval = () => {
        return interval
    }

    const [hideDialog, { setTrue: setHideDialog, setFalse: setShowDialog }] = useBoolean(true)
    const [initialDialogValues, setInitialDialogValues] = useState<Partial<TrackTimeSuggestionDto>>(undefined)
    const setHideDialogAndReset = () => {
        setHideDialog()
        resetTimeSuggestions()
    }

    useEffect(() => {
        // Only refresh after first load. If another user logs in, the state will be reset and this gets run again.
        // Any open dialog must be closed as its data is no longer applicable.
        if (!timeSuggestions.loading) {
            setHideDialogAndReset()
        }
    }, [currentUser])

    if (timeSuggestions.rejected) {
        return <MessageBar intent={'error'}>Er is een onbekende fout opgetreden</MessageBar>
    }

    if (timeSuggestions.loading) {
        return <Loading />
    }

    return (
        <Stack tokens={{ padding: 's1', childrenGap: 's1' }}>
            <Header isLoading={timeSuggestions.loading} interval={interval} setInterval={setInterval} />
            <TimeSuggestionList timeSuggestions={timeSuggestions} setInitialDialogValues={setInitialDialogValues} setShowDialog={setShowDialog} />
            <TimeSuggestionDialog
                hidden={hideDialog}
                setHidden={setHideDialogAndReset}
                initialValues={initialDialogValues}
                onDismiss={setHideDialogAndReset}
            />
        </Stack>
    )
}
